import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import DownloadIcon from "@mui/icons-material/Download";

import Langs, { Localize } from "../../../app/lang/langs";
import NetworkManager from "../../../app/managers/networkManager";
import { vlearnSurface, vlearnType } from "../vlearnUi";

/**
 * V-Learning — a lesson whose content is a PDF.
 *
 * Rendered in an `<iframe>` pointed at the file endpoint, which serves a PDF with
 * `Content-Disposition: inline`, so the browser's own viewer handles it. That viewer is
 * already accessible, already searchable and already printable; no PDF rendering library is
 * added to the bundle to reproduce it.
 *
 * The download link below is not a convenience. A handful of environments — older mobile
 * browsers, and anything with the built-in viewer disabled by policy — show an empty frame
 * and no error, and a learner staring at a blank rectangle needs a way out of it.
 */

const focusRing = {
    '&:focus-visible': {
        outline: '2px solid var(--orange, rgb(255,144,21))',
        outlineOffset: '2px'
    }
}

export default function PdfLesson({ lesson }) {

    const pdfFile = ((lesson && lesson.files) || []).find(file => file.kind === 'pdf') || null

    if (!pdfFile) {
        return (
            <Box sx={{ ...vlearnSurface.panel }}>
                <Typography sx={{ ...vlearnType.body }}>
                    <Langs str="VLEARN_PDF_MISSING" />
                </Typography>
            </Box>
        )
    }

    const url = NetworkManager.getInstance().getVLearnFileUrl(pdfFile.id)

    return (
        <Box>
            <Box sx={{
                ...vlearnSurface.card,
                /*
                 * Shorter on a phone. `68vh` of a 640px viewport is 435px of iframe with the
                 * fallback text and the download button below the fold — and an iframed PDF
                 * is exactly where a phone is most likely to show nothing at all (iOS Safari
                 * renders the first page and refuses to scroll inside the frame; Android
                 * Chrome offers a download instead of a viewer). Keeping the escape hatch
                 * visible next to the frame matters more here than the extra 80px of preview.
                 */
                height: { xs: '52vh', sm: 'clamp(420px, 68vh, 900px)' },
                backgroundColor: 'var(--lightGrey, #ededed)',
                '& iframe': { display: 'block', width: '100%', height: '100%', border: 0 }
            }}>
                <iframe
                    src={url}
                    title={Localize('VLEARN_PDF_FRAME_TITLE').replace('%title%', lesson.title || '')}
                />
            </Box>

            <Box sx={{
                mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1.5,
                alignItems: 'center', justifyContent: 'space-between'
            }}>
                <Typography sx={{ ...vlearnType.meta, maxWidth: '52ch' }}>
                    <Langs str="VLEARN_PDF_FALLBACK" />
                </Typography>
                <Button
                    size="small"
                    variant="outlined"
                    component="a"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                    // Full width on a phone: when the frame above shows nothing this is
                    // the only way to reach the document, so it should not be a 30px
                    // outlined button tucked under a paragraph.
                    sx={{
                        ...focusRing, textTransform: 'none', fontSize: '0.8rem',
                        minHeight: 44, width: { xs: '100%', sm: 'auto' },
                        flexShrink: 0, borderColor: 'rgba(0,0,0,0.18)', color: 'var(--text-primary, #111318)'
                    }}
                >
                    <Langs str="VLEARN_DOWNLOAD" />
                </Button>
            </Box>
        </Box>
    )
}
